import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSubscriptionSearchParams } from '../../../../extractFromState';
import * as searchActions from '../../../../constants/searchActions';
import { chooseDefaultSearchValue, selectSubscriptions, selectSubscriptionSystem } from '../../../../selectors/tools';
import SearchResults from './Common/SearchResults/SearchResults';
import DTSearchView from '../../Common/DTSearchView/DTSearchView';
import {
  searchCommonSubscriptions,
  setSearchType,
  setSearchValue,
  unSelectAllSubscriptionsInDecisionTree,
} from '../../../../actions/tools/CommonSubscriptionActions';
import { searchSubscriptionsViaSimplrOrder } from '../../../../actions/tools/DecisionTree/SimplrOrderActions';
import { displayPopupMessageInformation } from '../../../tools/SearchPage/searchUtils';
import { emailRegex } from '../../../../util';
import { SUBSCRIPTION_SYSTEMS } from '../../../../constants/integrations';
import { showErrorMessage } from '../../../../util/errorPopupUtil';
import { resetShowConfirmationPage } from '../../../../actions/tools/OrdersSearchActions';
import SearchOptions from './Common/SearchOptions/SearchOptions';
import NoSearchResults from './Common/NoSarchResults/NoSearchResults';

export const SUBSCRIPTION_SEARCH_TYPES = {
  [searchActions.SIMPLR_ORDER_SUBSCRIPTION_EMAIL]: 'email',
  [searchActions.COMMON_SUBSCRIPTION_EMAIL]: 'email',
};

export const DTSubscriptionSearchView = ({
  onNextButtonClick,
  isNextButtonActive,
  isSearchBothOrdersAndSubscriptions = false,
  isShowMacroButton = false,
}) => {
  const dispatch = useDispatch();

  const subscriptionSystem = useSelector(selectSubscriptionSystem);
  const searchParams = useSelector(getSubscriptionSearchParams);
  const defaultSearchValue = useSelector(chooseDefaultSearchValue);
  const searchSubscription = useSelector(selectSubscriptions);

  useEffect(() => {
    return () => {
      dispatch(setSearchType(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (defaultSearchValue && !searchParams.searchValue) {
      dispatch(setSearchValue(defaultSearchValue));
    }
  }, [defaultSearchValue, searchParams.searchValue, dispatch]);

  useEffect(() => {
    async function handleSearchSubscriptionsViaIntegration({ searchType, searchValue }) {
      switch (subscriptionSystem) {
        case SUBSCRIPTION_SYSTEMS.COMMON:
          return await dispatch(searchCommonSubscriptions(searchValue));
        case SUBSCRIPTION_SYSTEMS.SIMPLR_ORDER:
          return await dispatch(searchSubscriptionsViaSimplrOrder(SUBSCRIPTION_SEARCH_TYPES[searchType], searchValue));
        default:
          showErrorMessage(`Sorry, subscription system ${subscriptionSystem} is not found.`);
      }
    }

    async function handleSearchSubscriptions() {
      let { searchType, searchValue } = searchParams;

      if (!searchValue) {
        return displayPopupMessageInformation('Please enter at least one letter');
      }
      if (!searchType) {
        return displayPopupMessageInformation('Please select all options');
      }
      if (SUBSCRIPTION_SEARCH_TYPES[searchType] === 'email' && !emailRegex.test(searchValue)) {
        return displayPopupMessageInformation('Please enter a valid email address');
      }
      if (SUBSCRIPTION_SEARCH_TYPES[searchType] === 'phoneNumber') {
        searchValue = searchValue.replace(/\D/g, '');
        if (!/^[0-9]{11}$/.test(searchValue)) {
          return displayPopupMessageInformation('Phone number must be 11 digits, i.e. "18001234567"');
        }
      }

      dispatch(resetShowConfirmationPage());
      dispatch(unSelectAllSubscriptionsInDecisionTree());
      await handleSearchSubscriptionsViaIntegration({ searchType, searchValue });
    }

    if (searchParams.searchValue) {
      handleSearchSubscriptions();
    }
  }, [dispatch, isSearchBothOrdersAndSubscriptions, searchParams, subscriptionSystem]);

  const handleSearchSubmit = (value) => {
    dispatch(setSearchValue(value));
  };

  return (
    <DTSearchView
      isNextButtonActive={isNextButtonActive}
      onNextButtonClick={onNextButtonClick}
      handleSearchSubmit={handleSearchSubmit}
      searchType={searchParams.searchType}
      searchValue={searchParams.searchValue}
      defaultValue={defaultSearchValue}
      dataToDisplay={searchSubscription}
      searchPageSelectComponent={<SearchOptions />}
      searchResultsComponent={<SearchResults isSearchBothOrdersAndSubscriptions={isSearchBothOrdersAndSubscriptions} />}
      noDataMessageComponent={
        <NoSearchResults
          isSearchBothOrdersAndSubscriptions={isSearchBothOrdersAndSubscriptions}
          isShowMacroButton={isShowMacroButton}
        />
      }
    />
  );
};
